import React, { useMemo } from "react";
import { IconListSearch } from "@tabler/icons-react";
import { Box, Group, Text } from "@mantine/core";

interface TOCLink {
  label: string;
  link: string; // e.g. "/docs/balances-and-capacities/inbound-capacity"
  order?: number;
}

interface TableOfContentsProps {
  links: TOCLink[];
}

interface TOCItemProps {
  link: TOCLink;
  isActive: boolean;
}

const TOCItem: React.FC<TOCItemProps> = ({ link, isActive }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  // Handle click to scroll to article instead of navigating
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Extract article slug from the link
    const articleSlug = link.link.split("/").pop();
    if (articleSlug) {
      // Find the element with the matching ID
      const targetElement = document.getElementById(articleSlug);
      if (targetElement) {
        // Update URL without page reload
        window.history.pushState(null, "", link.link);

        // Scroll to the element with offset for header
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        // Update document title if needed
        const articleTitle = targetElement.querySelector("h3")?.textContent;
        if (articleTitle) {
          const pageTitle = document.title.split(" - ").slice(1).join(" - ");
          document.title = `${articleTitle} - ${pageTitle}`;
        }
      }
    }
  };

  // Container styles for each row (border, padding, etc.)
  const containerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    borderLeft: "1px solid var(--mantine-color-dark-4)",
    borderTopRightRadius: "var(--mantine-radius-sm)",
    borderBottomRightRadius: "var(--mantine-radius-sm)",
    padding: "var(--mantine-spacing-xs)",
    paddingLeft: `calc(${link.order ?? 1} * var(--mantine-spacing-md))`,
    marginBottom: "4px", // Small gap between rows
    backgroundColor: isHovered
      ? isActive
        ? "var(--mantine-color-blue-light)"
        : "var(--mantine-color-dark-6)"
      : isActive
      ? "var(--mantine-color-blue-light)"
      : "transparent",
    fontWeight: isActive ? 500 : undefined,
    borderLeftColor: isActive
      ? "var(--mantine-color-blue-4)"
      : "var(--mantine-color-dark-4)",
    color: isActive
      ? "var(--mantine-color-blue-4)"
      : "var(--mantine-color-text)",
    cursor: "default", // Prevent cursor change on container
  };

  // Only the text is clickable – no padding, so blank space is not clickable
  const anchorStyle: React.CSSProperties = {
    display: "inline",
    textDecoration: "none",
    color: isActive
      ? "var(--mantine-color-blue-4)"
      : "var(--mantine-color-text)",
    lineHeight: 1.2,
    fontSize: "var(--mantine-font-size-sm)",
    margin: "0 4px",
    cursor: "pointer", // Indicate that text is clickable
  };

  return (
    // Outer container – not clickable
    <Box
      style={containerStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Actual anchor – only text is clickable */}
      <Box<"a">
        component="a"
        href={link.link}
        style={anchorStyle}
        onClick={handleClick}
      >
        {link.label}
      </Box>
    </Box>
  );
};

export function TableOfContents({ links }: TableOfContentsProps) {
  // Current pathname and hash for active link detection
  const currentPathname = useMemo(() => window.location.pathname, []);
  const currentHash = useMemo(() => window.location.hash.slice(1), []);

  return (
    <Box>
      {/* Uncomment below if you want a header for TOC */}
      {/* 
      <Group mb="md">
        <IconListSearch size={18} stroke={1.5} />
        <Text>Table of contents</Text>
      </Group> 
      */}
      {links.map((item) => {
        // Determine if this link is active
        // Active if: pathname matches the full link OR hash matches the article slug
        const articleSlug = item.link.split("/").pop();
        const isActive =
          currentPathname === item.link ||
          (articleSlug && currentHash === articleSlug);
        return <TOCItem key={item.link} link={item} isActive={isActive} />;
      })}
    </Box>
  );
}
